/**
 * InvoicePreviewDialog
 * Reusable preview dialog for an invoice / proforma document.
 * Shared between PlatiTab (client) and SuperAdminDashboard (master view).
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText } from "lucide-react";

export default function InvoicePreviewDialog({ doc, onClose, footer }) {
  if (!doc) return null;
  const isProforma = doc.invoice_doc_type === "proforma";
  const docNo = `${doc.series}${String(doc.number).padStart(4, "0")}`;
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="invoice-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            {isProforma ? "PROFORMĂ" : "FACTURĂ FISCALĂ"} {docNo}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="border-2 rounded-lg p-4 sm:p-6 bg-white print:shadow-none"
            id="invoice-preview-content"
          >
            <div className="flex justify-between items-start border-b-2 border-teal-600 pb-3 mb-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-teal-700">
                  {isProforma ? "PROFORMĂ" : "FACTURĂ FISCALĂ"}
                </h2>
                <p className="text-sm">
                  Seria <strong>{doc.series}</strong> Nr.{" "}
                  <strong>{String(doc.number).padStart(4, "0")}</strong>
                </p>
                <p className="text-xs text-slate-500">Data: {doc.invoice_date}</p>
                {doc.proforma_due_at && (
                  <p className="text-xs text-red-600 font-semibold">
                    Scadență:{" "}
                    {doc.proforma_due_at.slice(0, 16).replace("T", " ")}
                  </p>
                )}
              </div>
              <Badge
                className={
                  doc.payment_status === "achitata"
                    ? "bg-green-600"
                    : "bg-amber-600"
                }
              >
                {doc.payment_status === "achitata"
                  ? "✓ ACHITATĂ"
                  : "⏰ DE ACHITAT"}
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-50 p-3 rounded">
                <p className="font-bold text-xs text-slate-500 uppercase mb-1">
                  Furnizor
                </p>
                <p className="font-semibold">{doc.supplier_name}</p>
                <p className="text-xs">CUI: {doc.supplier_cui}</p>
                <p className="text-xs text-slate-600">{doc.supplier_address}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded">
                <p className="font-bold text-xs text-slate-500 uppercase mb-1">
                  Client
                </p>
                <p className="font-semibold">{doc.client_name}</p>
                <p className="text-xs">CUI: {doc.client_cui || "—"}</p>
                <p className="text-xs text-slate-600">
                  {doc.client_address || ""}
                </p>
              </div>
            </div>

            <table className="w-full border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="p-2 text-left border">Denumire</th>
                  <th className="p-2 text-center border w-12">UM</th>
                  <th className="p-2 text-right border w-16">Cant.</th>
                  <th className="p-2 text-right border w-24">Preț unit.</th>
                  <th className="p-2 text-right border w-16">TVA</th>
                  <th className="p-2 text-right border w-24">Total</th>
                </tr>
              </thead>
              <tbody>
                {(doc.items || []).map((it, i) => (
                  <tr key={i}>
                    <td className="p-2 border">{it.name}</td>
                    <td className="p-2 text-center border">{it.um}</td>
                    <td className="p-2 text-right border">{it.quantity}</td>
                    <td className="p-2 text-right border">
                      {Number(it.unit_price).toFixed(2)}
                    </td>
                    <td className="p-2 text-right border">
                      {Number(it.vat_percent).toFixed(0)}%
                    </td>
                    <td className="p-2 text-right border font-semibold">
                      {Number(it.total).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="text-right mt-3 space-y-1 text-sm">
              <p>
                Subtotal:{" "}
                <strong>{Number(doc.subtotal || 0).toFixed(2)} RON</strong>
              </p>
              <p>
                TVA:{" "}
                <strong>{Number(doc.vat_amount || 0).toFixed(2)} RON</strong>
              </p>
              <p className="text-lg sm:text-xl text-red-600">
                TOTAL:{" "}
                <strong>{Number(doc.total || 0).toFixed(2)} RON</strong>
              </p>
            </div>
            <p className="text-[10px] text-slate-500 italic mt-3 text-center">
              {doc.notes || ""}
            </p>
            <p className="text-[10px] text-slate-400 text-center mt-2">
              Document generat prin{" "}
              <a href="https://e-tesa.ro" className="text-teal-600 underline">
                e-Tesa.ro
              </a>
            </p>
          </div>

          {footer ? (
            footer
          ) : (
            <div className="flex justify-end pt-3 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                data-testid="invoice-preview-close"
              >
                Închide
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
